import type { RefObject } from "react";
import { useCallback, useState } from "react";

import type {
  CdropdownAlign,
  CdropdownMode,
  CdropdownOpenDirection,
  DropdownPosition,
} from "./Cdropdown.types";
import { INITIAL_POSITION, resolveDropdownPosition } from "./positioning";

interface UseDropdownPositionStateInput {
  mode: CdropdownMode;
  anchorRect: DOMRect | null | undefined;
  triggerRef: RefObject<HTMLDivElement | null>;
  anchorRef: RefObject<HTMLElement | null> | undefined;
  align: CdropdownAlign;
  width: number | string;
  matchAnchorWidth: boolean;
  maxHeight: number | undefined;
  measuredHeight: number | null;
  openDirection: CdropdownOpenDirection;
  optionsLength: number;
}

interface UseDropdownPositionStateResult {
  anchorMeasuredWidth: number | null;
  dropdownPosition: DropdownPosition;
  updateDropdownPosition: () => void;
}

export function useDropdownPositionState(
  input: UseDropdownPositionStateInput,
): UseDropdownPositionStateResult {
  const { mode, anchorRect, triggerRef, anchorRef, align, width } = input;
  const { matchAnchorWidth, maxHeight, measuredHeight } = input;
  const { openDirection, optionsLength } = input;
  const [anchorMeasuredWidth, setAnchorMeasuredWidth] = useState<number | null>(
    null,
  );
  const [dropdownPosition, setDropdownPosition] =
    useState<DropdownPosition>(INITIAL_POSITION);

  const updateDropdownPosition = useCallback(() => {
    const resolvedPosition = resolveDropdownPosition({
      mode,
      anchorRect,
      triggerElement: triggerRef.current,
      horizontalAnchorElement: anchorRef?.current,
      align,
      width,
      matchAnchorWidth,
      maxHeight,
      measuredHeight,
      openDirection,
      optionsLength,
    });

    if (!resolvedPosition) {
      return;
    }

    if (resolvedPosition.anchorMeasuredWidth !== null) {
      setAnchorMeasuredWidth(resolvedPosition.anchorMeasuredWidth);
    }

    setDropdownPosition(resolvedPosition.position);
  }, [
    align,
    anchorRect,
    anchorRef,
    matchAnchorWidth,
    maxHeight,
    measuredHeight,
    mode,
    openDirection,
    optionsLength,
    triggerRef,
    width,
  ]);

  return {
    anchorMeasuredWidth,
    dropdownPosition,
    updateDropdownPosition,
  };
}
